import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { map, pluck, takeUntil } from 'rxjs/operators';
import { BroadcastService, NewrelicInteractionService, AuthenticationService, User } from '@webframework/client-core';
import * as fromIndex from './reducers';
import { CoreService } from '../../../../libs/shared/src/lib/Services/core.service';
import { LicenseSocketService, ChannelState } from './services/license-socket.service';

/**
 * The root component of the application
 */
@Component({
  selector: 'gs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationComponent implements OnDestroy {
  items$: Observable<MenuItem[]>;
  showSidebar$: Observable<Boolean>;
  channelState$: Observable<ChannelState | null>;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<fromIndex.State>,
    private newrelicInteractionService: NewrelicInteractionService,
    public coreService: CoreService,
    private cdRef: ChangeDetectorRef,
    private broadcastService: BroadcastService,
    private licenseSocketService: LicenseSocketService,
    private authService: AuthenticationService
  ) {
    this.store.dispatch({
      type: '[Breadcrumb]: Add Breadcrumb',
      payload: {
        id: 'homeMenuItem',
        label: 'Home',
        routerLink: ['home'],
      },
    });

    this.items$ = this.store.pipe(
      select(fromIndex.getBreadcrumbItem),
      map((items: MenuItem[]) => {
        return items;
      })
    );

    this.showSidebar$ = this.store.select("notificationBar").pipe(
      pluck("isVisible")
    );

    this.channelState$ = this.licenseSocketService.getChannelState();

    this.broadcastService.on('showNotifBar').subscribe((event: any) => {
      this.store.dispatch({
        type: "[NotificationBar] Show"
      })
    });

    // Initialize WebSocket connection after login
    this.authService.getUser().subscribe(async (user: User) => {
      if (user) {
        try {
          // Connect to WebSocket
          this.licenseSocketService.connect();

          console.log('user', user);

          // Join license channel with user data
          await this.licenseSocketService.joinLicenseChannel({
            userId: user.prefUserName.slice(0, 10),
            appName: 'lesmes',
            features: ['OPWPACKAGE'],
            appEnv: 'prd',
            licenseTag: `lesmes_${user.prefUserName.slice(0, 10)}`,
          });

          // Start heartbeat
          this.licenseSocketService.startHeartbeat();
        } catch (error) {
          console.error('Failed to initialize license connection:', error);
        }
      } else {
        this.licenseSocketService.disconnect();
      }
    });
  }

  getTooltipContent(state: ChannelState): string {
    const status = state.licensesAssigned ? 'Activa' : 'Obteniendo';
    const expirationDate = state.licenseExpirationDate ? 
      new Date(state.licenseExpirationDate).toLocaleString('es-ES') : 
      'No disponible';
    
    const features = state.licensesAssigned ? 
      Object.keys(state.licensesAssigned).join(', ') : 
      'Ninguna';

    return `
      <div class="license-details">
        <h4>Detalles de la Licencia</h4>
        <p><strong>Estado:</strong> ${status}</p>
        <p><strong>Vencimiento:</strong> ${expirationDate}</p>
        <p><strong>Características:</strong> ${features}</p>
        <p><strong>Ambiente:</strong> ${state.appEnv}</p>
        <p><strong>Cantidad de Clientes:</strong> ${state.clientIds.length}</p>
      </div>
    `;
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
